
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Globe, Phone, Star, ArrowLeft, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { useLeadFilters } from '@/hooks/useLeadFilters';
import SharedLeadsToolbar from '@/components/shared-leads/SharedLeadsToolbar';
import SharedLeadsTable from '@/components/shared-leads/SharedLeadsTable';

const SharedLeads: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();

  const { data: shareInfo } = useQuery({
    queryKey: ['lead-share-info', token],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_shares')
        .select('created_at, created_by, expires_at, company_id')
        .eq('token', token!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!token,
  });

  const { data: creatorProfile } = useQuery({
    queryKey: ['share-creator', shareInfo?.created_by],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('first_name, last_name, email')
        .eq('id', shareInfo!.created_by)
        .maybeSingle();
      return data;
    },
    enabled: !!shareInfo?.created_by,
  });

  const { data: leads, isLoading, error } = useQuery({
    queryKey: ['shared-leads', token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_shared_leads', { p_token: token! });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!token,
  });

  const filters = useLeadFilters(leads || []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !leads || leads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <h2 className="text-xl font-semibold text-foreground">
          {error ? 'Error loading shared leads' : 'No leads found'}
        </h2>
        <p className="text-sm text-muted-foreground">
          This link may have expired or is invalid.
        </p>
        <Button asChild variant="outline">
          <Link to="/app"><ArrowLeft className="h-4 w-4 mr-2" /> Back to App</Link>
        </Button>
      </div>
    );
  }

  const creatorName = creatorProfile
    ? [creatorProfile.first_name, creatorProfile.last_name].filter(Boolean).join(' ') || creatorProfile.email
    : 'Someone';

  const isSameCompany = profile?.company_id && shareInfo?.company_id && profile.company_id === (shareInfo as any).company_id;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Shared Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Shared by {creatorName}
            {shareInfo?.created_at && ` on ${format(new Date(shareInfo.created_at), 'MMM d, yyyy')}`}
            {' · '}{leads.length} lead{leads.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/app"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Link>
        </Button>
      </div>

      <SharedLeadsToolbar
        search={filters.search}
        onSearchChange={filters.setSearch}
        sortDir={filters.sortDir}
        onSortToggle={() => filters.setSortDir(filters.sortDir === 'asc' ? 'desc' : 'asc')}
        selectedCategories={filters.selectedCategories}
        onCategoriesChange={filters.setSelectedCategories}
        availableCategories={filters.availableCategories}
        view={filters.view}
        onViewChange={filters.setView}
        resultCount={filters.filteredLeads.length}
      />

      {filters.view === 'table' ? (
        <SharedLeadsTable
          leads={filters.filteredLeads}
          onRowClick={(id) => isSameCompany && navigate(`/leads/${id}`)}
          clickable={!!isSameCompany}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filters.filteredLeads.map((lead: any) => {
            const cardContent = (
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-foreground text-sm leading-tight line-clamp-2">
                    {lead.name}
                  </h3>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {lead.rating && (
                      <Badge variant="secondary" className="gap-1 text-xs">
                        <Star className="h-3 w-3 fill-current" />
                        {lead.rating}
                      </Badge>
                    )}
                    {isSameCompany && (
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {lead.category && (
                  <Badge variant="outline" className="text-xs">{lead.category}</Badge>
                )}

                <div className="space-y-1.5 text-xs text-muted-foreground">
                  {lead.address && (
                    <div className="flex items-start gap-1.5">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{lead.address}</span>
                    </div>
                  )}
                  {lead.website && (
                    <div className="flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate text-primary">
                        {lead.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </span>
                    </div>
                  )}
                  {lead.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      <span>{lead.phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            );

            if (isSameCompany) {
              return (
                <Link key={lead.id} to={`/leads/${lead.id}`}>
                  <Card className="overflow-hidden hover:shadow-md hover:border-primary/30 transition-all cursor-pointer">
                    {cardContent}
                  </Card>
                </Link>
              );
            }

            return (
              <Card key={lead.id} className="overflow-hidden hover:shadow-md transition-shadow">
                {cardContent}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SharedLeads;
